import { useMemo } from "react";
import { cn } from "@/lib/cn";
import ManagerRecommendations from "./ManagerRecommendations";
import ManagerRattings from "./ManagerRattings";
import {
  useGetOwnAppraisalDetailsQuery,
  useGetTeamAppraisalDetailsQuery,
  useGetAdminAppraisalDetailsQuery,
} from "@/app/features/employee/performance/performance.api";
import { useGetAllDesignationsQuery } from "@/app/features/employee/emp-dashboard/emp-dashboard.api";
import CustomDrawer from "@/components/CustomDrawer";
import WithLoader from "@/components/WithLoader";
import { PaperclipIcon } from "@/icons";
import { Flex } from "antd";

interface ViewAppraisalProps {
  open: boolean;
  onClose: () => void;
  planName?: string;
  mappingId?: string;
  source?: "OWN" | "TEAM";
  from?: "manager" | "admin";
}

export default function ViewAppraisal({
  open,
  onClose,
  planName,
  mappingId,
  source = "OWN",
  from = "manager",
}: ViewAppraisalProps) {
  const isAdmin = from === "admin";
  const isTeam = source === "TEAM" && !isAdmin;
  const isOwn = !isTeam && !isAdmin;

  // Own details query
  const { data: ownDetailRes, isFetching: isOwnFetching } =
    useGetOwnAppraisalDetailsQuery(mappingId || "", {
      skip: !mappingId || !open || !isOwn,
    });

  // Team details query
  const { data: teamDetailRes, isFetching: isTeamFetching } =
    useGetTeamAppraisalDetailsQuery(mappingId || "", {
      skip: !mappingId || !open || !isTeam,
    });

  // Admin details query
  const { data: adminDetailRes, isFetching: isAdminFetching } =
    useGetAdminAppraisalDetailsQuery(mappingId || "", {
      skip: !mappingId || !open || !isAdmin,
    });

  const { data: designationsData } = useGetAllDesignationsQuery(
    { params: { status: "active" } },
    { skip: !open },
  );

  const designationMap = useMemo(() => {
    const map: Record<string, string> = {};
    designationsData?.data?.data?.forEach((item: any) => {
      map[item.id] = item.name;
    });
    return map;
  }, [designationsData]);

  const detailRes = isAdmin
    ? adminDetailRes
    : isTeam
      ? teamDetailRes
      : ownDetailRes;
  const isFetching = isAdmin
    ? isAdminFetching
    : isTeam
      ? isTeamFetching
      : isOwnFetching;

  const kpis = detailRes?.data?.employeeKpis || [];
  const attachments = detailRes?.data?.attachments || [];
  const managerMappings = useMemo(() => {
    return detailRes?.data?.managerMappings || [];
  }, [detailRes?.data?.managerMappings]);
  // Support both snake_case and camelCase field names from the API
  const employeeComment = (detailRes?.data as any)?.employeeComment || detailRes?.data?.employee_comment;
  const adminRecommendation = detailRes?.data?.adminRecommendation;

  const allRecommendations = useMemo(() => {
    const list = [...(managerMappings || [])];
    if (isAdmin && adminRecommendation) {
      list.push({
        id: "admin-rec",
        manager: {
          firstName: "Admin",
          lastName: "Recommendation",
        },
        managerRecommendation: adminRecommendation,
      } as any);
    }
    return list;
  }, [managerMappings, isAdmin, adminRecommendation]);

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={onClose}
      title={`${planName} - View Appraisal`}
    >
      <WithLoader
        isLoading={isFetching}
        className={cn("rounded-lg", isFetching && "h-[calc(100vh_-_105px)]")}
      >
        <div className="space-y-8">
          {/* KPI List */}
          <div className="border border-theme-gray-700 rounded-xl p-6">
            <h4 className="text-white text-base font-semibold mb-6">
              KPI List
            </h4>
            <div className="space-y-6">
              {kpis.map((kpi: any, index: number) => (
                <div key={kpi.id || index} className="space-y-4">
                  {index > 0 && (
                    <div className="border-t border-dashed border-theme-gray-700 pt-6" />
                  )}
                  <Flex justify="space-between" align="center">
                    <h5 className="text-white text-sm font-medium">
                      KPI -{index + 1}
                    </h5>
                  </Flex>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block">
                        Name
                      </label>
                      <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-2.5 text-white text-sm min-h-[44px]">
                        {kpi.kpiName}
                      </div>
                    </div>
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block">
                        Weightage(%)
                      </label>
                      <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-2.5 text-white text-sm min-h-[44px]">
                        {kpi.weightage}%
                      </div>
                    </div>
                  </div>

                  {kpi.details && (
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block">
                        Details
                      </label>
                      <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[60px] whitespace-pre-wrap">
                        {kpi.details}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block font-semibold tracking-wider">
                        Employee Self Evaluation
                      </label>
                      <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[80px] whitespace-pre-wrap">
                        {kpi.selfEvaluation || "--"}
                      </div>
                    </div>
                    <div>
                      <label className="text-theme-gray-400 text-xs mb-1.5 block font-semibold tracking-wider">
                        Rating Scale (1-5)
                      </label>
                      <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-2.5 text-white text-sm">
                        {kpi.selfRating || "0"}
                      </div>
                    </div>
                  </div>

                  {/* Manager Evaluations */}
                  <ManagerRattings ratings={kpi.managerRatings || []} />
                </div>
              ))}
            </div>
          </div>

          {/* Manager Recommendations */}
          <ManagerRecommendations
            managerMappings={allRecommendations}
            designationMap={designationMap}
          />

          {/* Employee Overall Comment */}
          {employeeComment && (
            <div className="space-y-4">
              <h4 className="text-white text-base font-semibold">
                Employee Overall Comment
              </h4>
              <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[80px] whitespace-pre-wrap">
                {employeeComment}
              </div>
            </div>
          )}

          {/* Manager Comments */}
          {managerMappings.some((m: any) => m.appraisalComment) && (
            <div className="space-y-4">
              <h4 className="text-white text-base font-semibold">
                Manager Comments
              </h4>
              <div className="space-y-4">
                {managerMappings.map((mapping: any, idx: number) => {
                  const comment = mapping.appraisalComment;
                  if (!comment) return null;

                  const managerName = mapping.manager
                    ? `${mapping.manager.firstName || ""} ${mapping.manager.lastName || ""}`.trim()
                    : "";

                  return (
                    <div key={mapping.id || idx} className="space-y-2">
                      <label className="text-theme-gray-400 text-xs block font-semibold tracking-wider">
                        Line Manager - {idx + 1}{" "}
                        {managerName ? `(${managerName})` : ""}
                      </label>
                      <div className="bg-theme-slate-800 border border-white/30 rounded-lg px-4 py-3 text-white text-sm min-h-[100px] whitespace-pre-wrap">
                        {comment}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Attachment */}
          <div>
            <h4 className="text-white text-base font-semibold mb-4">
              Attachment
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {attachments.length > 0 ? (
                attachments.map((file: any, index: number) => (
                  <a
                    key={index}
                    href={file.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="flex items-center gap-3 w-full px-4 py-2.5 rounded-lg bg-slate-800 border border-gray-700 hover:border-theme-primary hover:bg-slate-700 transition-all duration-200 group no-underline"
                  >
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-theme-primary/10 flex-shrink-0">
                      <PaperclipIcon size={16} className="text-theme-primary" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-white text-sm font-medium group-hover:text-theme-primary transition-colors duration-200 truncate">
                        {file.fileName || "attachment.pdf"}
                      </span>
                      <span className="text-gray-500 text-xs">
                        Click to download
                      </span>
                    </div>
                  </a>
                ))
              ) : (
                <div className="col-span-full flex items-center gap-3 w-full px-4 py-2.5 rounded-lg bg-slate-800/50 border border-gray-700 opacity-60 cursor-not-allowed group no-underline">
                  <div className="flex items-center justify-center w-8 h-8 rounded-md bg-gray-700/30 flex-shrink-0">
                    <PaperclipIcon size={16} className="text-gray-500" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-gray-400 text-sm font-medium">
                      No attachment
                    </span>
                    <span className="text-gray-600 text-xs">Not available</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </WithLoader>
    </CustomDrawer>
  );
}
